import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { personDetailUrl, rememberListScroll, useRestoreListScroll } from '@/lib/personDetailLink';
import { Trophy } from 'lucide-react';

import { PageHeader } from '@/components/AppShell';
import { DataTable, Pagination, type Column } from '@/components/DataTable';
import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Select,
  TableSkeleton,
} from '@/components/ui';
import { count, countryFlag, countryName, decimal } from '@/lib/format';
import { useResource } from '@/lib/hooks';
import { useDataScope } from '@/lib/dataPurpose';

type Kind = 'prime_score' | 'pillar' | 'metric';
type Option = { code: string; label_de: string; label_en: string };
type Category = Option & {
  kind: Kind;
  unit: string | null;
  direction: 'higher_is_better' | 'lower_is_better';
};
type Options = {
  categories: Category[];
  countries: { code: string; continent: string | null; player_count: number }[];
  continents: Option[];
  locations: Option[];
  positions: Option[];
  age_groups: Option[];
  teams: { id: string; name: string }[];
};
type Entry = {
  rank: number;
  user_id: string;
  player_code: string;
  display_name: string;
  country: string | null;
  residence_country: string | null;
  age_group: string | null;
  position: string | null;
  score: number;
  rating: number | null;
  prime_score: number | null;
  metric_value: number | null;
  metric_unit: string | null;
};
type Board = {
  category: string;
  total: number;
  offset: number;
  limit: number;
  entries: Entry[];
};
const BASE = '/api/v1/admin/leaderboard';
const SIZES = [25, 50, 100] as const;
const label = (option: Option, language: string) =>
  language === 'en' ? option.label_en : option.label_de;

export function LeaderboardsPage() {
  const { t, i18n } = useTranslation();
  const { scope } = useDataScope();
  const [params, setParams] = useSearchParams();
  const options = useResource<Options>(`${BASE}/options`, { data_purpose: scope }, [scope]);
  if (options.error)
    return (
      <>
        <PageHeader
          title={t('leaderboards.title')}
          subtitle={t('leaderboards.subtitle')}
        />
        <div className="card">
          <ErrorState
            message={t('common.loadFailed')}
            onRetry={options.reload}
          />
        </div>
      </>
    );
  if (!options.data)
    return (
      <>
        <PageHeader
          title={t('leaderboards.title')}
          subtitle={t('leaderboards.subtitle')}
        />
        <div className="card">
          <TableSkeleton />
        </div>
      </>
    );
  return (
    <Content
      options={options.data}
      params={params}
      setParams={setParams}
      language={i18n.language}
      scope={scope}
    />
  );
}

function Content({
  options,
  params,
  setParams,
  language,
  scope,
}: {
  options: Options;
  params: URLSearchParams;
  setParams: ReturnType<typeof useSearchParams>[1];
  language: string;
  scope: ReturnType<typeof useDataScope>['scope'];
}) {
  const { t } = useTranslation();
  const routeLocation = useLocation();
  const source = `${routeLocation.pathname}${routeLocation.search}`;
  const category =
    options.categories.find((item) => item.code === params.get('category')) ??
    options.categories.find((item) => item.code === 'talentScore');
  const categoryCode = category?.code ?? 'talentScore';
  const team =
    options.teams.find((item) => item.id === params.get('team_id'))?.id ?? '';
  const country =
    options.countries.find((item) => item.code === params.get('country'))
      ?.code ?? '';
  const continent = country
    ? ''
    : (options.continents.find((item) => item.code === params.get('continent'))
        ?.code ?? '');
  const location =
    country || continent
      ? ''
      : (options.locations.find((item) => item.code === params.get('location'))
          ?.code ?? '');
  const geography = country
    ? `country:${country}`
    : continent
      ? `continent:${continent}`
      : location
        ? `location:${location}`
        : '';
  const position =
    options.positions.find((item) => item.code === params.get('position'))
      ?.code ?? '';
  const ageOptions = options.age_groups
    .filter((item) =>
      /^(?:[mw](?:[89]|1[0-9])|adult(?:_male|_female)?)$/.test(item.code),
    )
    .sort((a, b) => {
      const order = (code: string) =>
        code.startsWith('m') ? 0 : code.startsWith('w') ? 1 : 2;
      return (
        order(a.code) - order(b.code) ||
        Number(a.code.slice(1)) - Number(b.code.slice(1)) ||
        a.code.localeCompare(b.code)
      );
    });
  const ages = ageOptions
    .filter((item) =>
      (params.get('age_groups')?.split(',') ?? []).includes(item.code),
    )
    .map((item) => item.code);
  const requestedLimit = Number(params.get('limit'));
  const limit = SIZES.find((size) => size === requestedLimit) ?? 50;
  const requestedPage = Number(params.get('page'));
  const page =
    Number.isSafeInteger(requestedPage) &&
    requestedPage > 0 &&
    requestedPage <= Math.floor(Number.MAX_SAFE_INTEGER / limit)
      ? requestedPage
      : 1;
  const offset = (page - 1) * limit;
  const invalidFilter =
    (!!params.get('category') && params.get('category') !== categoryCode) ||
    (!!params.get('team_id') && params.get('team_id') !== team) ||
    (!!params.get('country') && params.get('country') !== country) ||
    (!!params.get('continent') && params.get('continent') !== continent) ||
    (!!params.get('location') && params.get('location') !== location) ||
    (!!params.get('position') && params.get('position') !== position) ||
    (params
      .get('age_groups')
      ?.split(',')
      .some((age) => !ages.includes(age)) ??
      false);
  const agePicker = useRef<HTMLDetailsElement>(null);
  function update(changes: Record<string, string | null>, resetPage = true) {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      if (resetPage) next.delete('page');
      return next;
    });
  }
  const board = useResource<Board>(
    BASE,
    {
      category: categoryCode,
      team_id: team || undefined,
      country: country || undefined,
      continent: continent || undefined,
      location: location || undefined,
      position: position || undefined,
      age_groups: ages.length ? ages.join(',') : undefined,
      offset,
      limit,
      data_purpose: scope,
    },
    [
      categoryCode,
      team,
      country,
      continent,
      location,
      position,
      ages.join(','),
      offset,
      limit,
      scope,
    ],
  );
  useRestoreListScroll(source, !invalidFilter && !board.loading && !board.error && Boolean(board.data));
  useEffect(() => {
    if (invalidFilter || board.loading || board.error || !board.data) return;
    const lastPage = Math.max(1, Math.ceil(board.data.total / limit));
    if (page > lastPage) {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (lastPage === 1) next.delete('page');
          else next.set('page', String(lastPage));
          return next;
        },
        { replace: true },
      );
    }
  }, [
    invalidFilter,
    board.loading,
    board.error,
    board.data,
    page,
    limit,
    setParams,
  ]);
  if (invalidFilter)
    return (
      <>
        <PageHeader title={t('leaderboards.title')} />
        <div className="card p-4">
          <p role="alert" className="mb-3 text-sm text-ink">
            {t('leaderboards.invalidFilter')}
          </p>
          <Button onClick={() => setParams(new URLSearchParams())}>
            {t('common.reset')}
          </Button>
        </div>
      </>
    );
  const ageLabel = (code: string) =>
    code === 'adult'
      ? t('leaderboards.adult')
      : code === 'adult_male'
        ? t('leaderboards.adultMale')
        : code === 'adult_female'
          ? t('leaderboards.adultFemale')
          : code.toUpperCase();
  const columns: Column<Entry>[] = [
    {
      key: 'rank',
      header: t('leaderboards.rank'),
      render: (row) => <strong>{count(row.rank, language)}</strong>,
    },
    {
      key: 'player',
      header: t('leaderboards.player'),
      render: (row) => (
        <div>
          <Link className="font-medium text-brand hover:underline" to={personDetailUrl(row.user_id, source)} onClick={() => rememberListScroll(source)}>{row.display_name}</Link>
          <div className="text-2xs text-ink-subtle">{row.player_code}</div>
        </div>
      ),
    },
    {
      key: 'country',
      header: t('leaderboards.country'),
      render: (row) =>
        row.residence_country ? (
          <span>
            <span aria-hidden>{countryFlag(row.residence_country)} </span>
            {countryName(row.residence_country, language)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'age',
      header: t('leaderboards.age'),
      render: (row) =>
        row.age_group
          ? row.age_group.startsWith('adult')
            ? '20+'
            : ageLabel(row.age_group)
          : '—',
    },
    {
      key: 'position',
      header: t('leaderboards.position'),
      render: (row) => {
        const item = options.positions.find(
          (candidate) => candidate.code === row.position,
        );
        return item ? label(item, language) : '—';
      },
    },
    {
      key: 'score',
      header: t(
        category?.kind === 'metric'
          ? 'leaderboards.result'
          : 'leaderboards.score',
      ),
      align: 'right',
      render: (row) => (
        <div>
          <div className="font-semibold text-ink">
            {category?.kind === 'metric' && row.metric_value !== null
              ? `${decimal(row.metric_value, language)} ${row.metric_unit ?? category.unit ?? ''}`
              : decimal(row.score, language)}
          </div>
          {category?.kind === 'metric' && (
            <div className="text-2xs text-ink-subtle">
              {t('leaderboards.points', {
                value: decimal(row.score, language),
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'rating',
      header: t('leaderboards.rating'),
      align: 'right',
      render: (row) =>
        row.rating === null ? '—' : count(row.rating, language),
    },
    ...(category?.kind === 'prime_score'
      ? []
      : [
          {
            key: 'prime',
            header: t('leaderboards.primeScore'),
            align: 'right' as const,
            render: (row: Entry) =>
              row.prime_score === null
                ? '—'
                : decimal(row.prime_score, language),
          },
        ]),
  ];
  return (
    <>
      <PageHeader
        title={t('leaderboards.title')}
        subtitle={t('leaderboards.subtitle')}
      />
      <div className="card">
        <p className="border-b border-line px-4 py-3 text-xs text-ink-subtle">
          {t(
            category?.kind === 'prime_score'
              ? 'leaderboards.primeHint'
              : category?.kind === 'metric'
                ? 'leaderboards.metricHint'
                : 'leaderboards.pillarHint',
          )}
        </p>
        <div className="grid gap-4 border-b border-line p-4 sm:grid-cols-2 xl:grid-cols-3">
          <Field
            label={t('leaderboards.category')}
            hint={
              category?.kind === 'metric'
                ? t(
                    category.direction === 'lower_is_better'
                      ? 'leaderboards.lowerBetter'
                      : 'leaderboards.higherBetter',
                  )
                : undefined
            }
          >
            {(id, describedBy) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                className="w-full"
                value={categoryCode}
                onChange={(event) =>
                  update({
                    category:
                      event.target.value === 'talentScore'
                        ? null
                        : event.target.value,
                  })
                }
              >
                <optgroup label={t('leaderboards.overall')}>
                  {options.categories
                    .filter((item) => item.kind === 'prime_score')
                    .map((item) => (
                      <option key={item.code} value={item.code}>
                        {label(item, language)}
                      </option>
                    ))}
                </optgroup>
                <optgroup label={t('leaderboards.pillars')}>
                  {options.categories
                    .filter((item) => item.kind === 'pillar')
                    .map((item) => (
                      <option key={item.code} value={item.code}>
                        {label(item, language)}
                      </option>
                    ))}
                </optgroup>
                <optgroup label={t('leaderboards.metrics')}>
                  {options.categories
                    .filter((item) => item.kind === 'metric')
                    .map((item) => (
                      <option key={item.code} value={item.code}>
                        {label(item, language)}
                      </option>
                    ))}
                </optgroup>
              </Select>
            )}
          </Field>
          <Field label={t('leaderboards.scope')}>
            {(id) => (
              <Select
                id={id}
                className="w-full"
                value={team}
                onChange={(event) => update({ team_id: event.target.value })}
              >
                <option value="">{t('leaderboards.allPlayers')}</option>
                {options.teams.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('leaderboards.geography')}
            hint={t('leaderboards.geographyHint')}
          >
            {(id) => (
              <Select
                id={id}
                className="w-full"
                value={geography}
                onChange={(event) => {
                  const [kind, code] = event.target.value.split(':');
                  update({
                    country: kind === 'country' ? code : null,
                    continent: kind === 'continent' ? code : null,
                    location: kind === 'location' ? code : null,
                  });
                }}
              >
                <option value="">{t('leaderboards.worldwide')}</option>
                <optgroup label={t('leaderboards.continents')}>
                  {options.continents.map((item) => (
                    <option key={item.code} value={`continent:${item.code}`}>
                      {label(item, language)}
                    </option>
                  ))}
                </optgroup>
                <optgroup label={t('leaderboards.countries')}>
                  {options.countries.map((item) => (
                    <option key={item.code} value={`country:${item.code}`}>
                      {countryName(item.code, language) ?? item.code}
                    </option>
                  ))}
                </optgroup>
                <optgroup label={t('leaderboards.regions')}>
                  {options.locations.map((item) => (
                    <option key={item.code} value={`location:${item.code}`}>
                      {label(item, language)}
                    </option>
                  ))}
                </optgroup>
              </Select>
            )}
          </Field>
          <Field label={t('leaderboards.position')}>
            {(id) => (
              <Select
                id={id}
                className="w-full"
                value={position}
                onChange={(event) => update({ position: event.target.value })}
              >
                <option value="">{t('leaderboards.anyPosition')}</option>
                {options.positions.map((item) => (
                  <option key={item.code} value={item.code}>
                    {label(item, language)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-ink">
              {t('leaderboards.ageGroups')}
            </span>
            <details
              ref={agePicker}
              className="relative"
              onKeyDown={(event) => {
                if (event.key === 'Escape' && agePicker.current) {
                  agePicker.current.open = false;
                  agePicker.current.querySelector('summary')?.focus();
                }
              }}
            >
              <summary className="cursor-pointer rounded-lg border border-line bg-surface-raised px-3 py-2 text-sm text-ink">
                {ages.length
                  ? t('leaderboards.selectedAges', { count: ages.length })
                  : t('leaderboards.allAges')}
              </summary>
              <div className="absolute z-10 mt-1 max-h-64 w-full min-w-48 overflow-y-auto rounded-lg border border-line bg-surface-raised p-2 shadow-lg">
                {ageOptions.map((item) => (
                  <label
                    key={item.code}
                    className="flex items-center gap-2 rounded px-2 py-1 text-sm text-ink hover:bg-surface-sunken"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-line text-brand"
                      checked={ages.includes(item.code)}
                      onChange={(event) =>
                        update({
                          age_groups: ageOptions
                            .filter((candidate) =>
                              candidate.code === item.code
                                ? event.target.checked
                                : ages.includes(candidate.code),
                            )
                            .map((candidate) => candidate.code)
                            .join(','),
                        })
                      }
                    />
                    {ageLabel(item.code)}
                  </label>
                ))}
                <Button
                  className="sticky bottom-0 mt-2 w-full bg-surface-raised"
                  onClick={() => {
                    if (agePicker.current) agePicker.current.open = false;
                  }}
                >
                  {t('leaderboards.done')}
                </Button>
              </div>
            </details>
          </div>
          <div className="flex items-end gap-2">
            <Field label={t('common.perPageLabel')}>
              {(id) => (
                <Select
                  id={id}
                  value={String(limit)}
                  onChange={(event) =>
                    update({
                      limit:
                        event.target.value === '50' ? null : event.target.value,
                    })
                  }
                >
                  {SIZES.map((size) => (
                    <option key={size} value={size}>
                      {t('common.perPage', { n: size })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button onClick={() => setParams(new URLSearchParams())}>
              {t('common.reset')}
            </Button>
          </div>
        </div>
        <div
          className="border-b border-line px-4 py-3 text-sm text-ink-muted"
          aria-live="polite"
        >
          {board.loading
            ? t('common.loading')
            : !board.error && board.data
              ? t('leaderboards.total', { count: board.data.total })
              : null}
        </div>
        {board.error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={board.reload} />
        ) : board.loading ? (
          <TableSkeleton columns={columns.length} />
        ) : board.data?.entries.length === 0 ? (
          <EmptyState
            icon={<Trophy className="h-8 w-8" />}
            title={t('leaderboards.emptyTitle')}
            body={t('leaderboards.emptyBody')}
            action={
              <Button onClick={() => setParams(new URLSearchParams())}>
                {t('common.reset')}
              </Button>
            }
          />
        ) : (
          <>
            <DataTable
              caption={t('leaderboards.title')}
              columns={columns}
              rows={board.data?.entries ?? []}
              rowKey={(row) => row.user_id}
            />
            <Pagination
              page={page}
              pageSize={limit}
              total={board.data?.total ?? 0}
              onPage={(next) =>
                update({ page: next === 1 ? null : String(next) }, false)
              }
            />
          </>
        )}
      </div>
    </>
  );
}
